import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faCheck,
  faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";
import NotificationItem from "./NotificationItem";
import RefreshButton from "../common/RefreshButton";
import { useNotification } from "../context/NotificationContext";
import { useUser } from "../context/UserContext";
import { useTranslation } from "../../../i18n/LanguageContext";
import EmailAnalysisAPI from "../services/EmailAnalysisAPI";
import "../css/Common.css";
import "../scamdetector/email/css/EmailAnalysisSimpleMobile.css";
import "./css/NotificationSimpleMobile.css";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function NotificationSimpleMobile({ setHeroContent }) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [
    verificationFailedNotificationId,
    setVerificationFailedNotificationId,
  ] = useState(null);
  const componentMountedRef = useRef(true);

  const { loggedInUser } = useUser();
  const { t } = useTranslation();
  const {
    notifications,
    loading,
    error,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotifications,
    refreshNotifications,
    unreadCount,
  } = useNotification();

  const safeNotifications = useMemo(
    () => (Array.isArray(notifications) ? notifications : []),
    [notifications],
  );
  const hasUnread = unreadCount > 0;

  // ── Lifecycle ──
  useEffect(() => {
    componentMountedRef.current = true;
    if (loggedInUser?.uuid) {
      loadNotifications();
    }
    return () => {
      componentMountedRef.current = false;
    };
  }, [loggedInUser, loadNotifications]);

  useEffect(() => {
    const handleCancelVerification = () => {
      setVerificationFailedNotificationId(null);
    };
    window.addEventListener(
      "cancel-notification-verification",
      handleCancelVerification,
    );
    return () => {
      window.removeEventListener(
        "cancel-notification-verification",
        handleCancelVerification,
      );
    };
  }, []);

  useEffect(() => {
    // Helper: find a notification matching an analysis key
    const findMatchedNotification = (analysisKey) => {
      if (!analysisKey || safeNotifications.length === 0) return null;
      const targetFilename = analysisKey.split("/").pop();
      return safeNotifications.find((n) => {
        try {
          const action =
            typeof n.actionData === "string"
              ? JSON.parse(n.actionData)
              : n.actionData;
          const key = action?.params?.analysisKey;
          return (
            key &&
            (key === analysisKey ||
              key.endsWith(targetFilename) ||
              analysisKey.endsWith(key.split("/").pop()))
          );
        } catch {
          return false;
        }
      });
    };

    // Check sessionStorage for a key stored before navigation (event was already fired)
    const pendingKey = sessionStorage.getItem("kinshield_failed_analysis_key");
    if (pendingKey && safeNotifications.length > 0) {
      sessionStorage.removeItem("kinshield_failed_analysis_key");
      const matched = findMatchedNotification(pendingKey);
      if (matched) {
        setVerificationFailedNotificationId(matched.notificationId);
      }
    }

    // Also handle real-time event (e.g. when component is already mounted)
    const handleAnalysisNotFound = (event) => {
      const { analysisKey } = event.detail || {};
      const matched = findMatchedNotification(analysisKey);
      if (matched) {
        setVerificationFailedNotificationId(matched.notificationId);
      }
    };
    window.addEventListener("email-analysis-not-found", handleAnalysisNotFound);
    return () => {
      window.removeEventListener(
        "email-analysis-not-found",
        handleAnalysisNotFound,
      );
    };
  }, [safeNotifications]);

  // ── Handlers ──
  const handleRefresh = useCallback(async () => {
    if (!componentMountedRef.current) return;
    setIsRefreshing(true);
    try {
      if (refreshNotifications) {
        await refreshNotifications();
      } else {
        await loadNotifications();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshNotifications, loadNotifications]);

  const handleMarkAsRead = useCallback(
    async (notificationId) => {
      if (!loggedInUser?.uuid || !componentMountedRef.current) return;
      try {
        await markAsRead([notificationId]);
      } catch (err) {
        console.error("Failed to mark notification as read:", err);
        throw err;
      }
    },
    [loggedInUser, markAsRead],
  );

  const handleDismiss = useCallback(
    async (notificationId) => {
      if (!loggedInUser?.uuid || !componentMountedRef.current) return;
      try {
        await dismissNotifications([notificationId]);
      } catch (err) {
        console.error("Failed to dismiss notification:", err);
        throw err;
      }
    },
    [loggedInUser, dismissNotifications],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    if (!loggedInUser?.uuid || !componentMountedRef.current) return;
    try {
      await markAllAsRead();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  }, [loggedInUser, markAllAsRead]);

  const handleNotificationAction = useCallback(
    async (actionData, notificationId) => {
      try {
        const parsedAction =
          typeof actionData === "string" ? JSON.parse(actionData) : actionData;

        if (parsedAction.type === "navigate" && parsedAction.route) {
          if (
            parsedAction.route === "EmailAnalysis" &&
            parsedAction.params?.analysisKey
          ) {
            const exists = await EmailAnalysisAPI.checkAnalysisExists(
              parsedAction.params.analysisKey,
            );
            if (!exists) {
              setVerificationFailedNotificationId(notificationId);
              return;
            }
          }

          let normalizedRoute = parsedAction.route;
          if (normalizedRoute === "EmailAnalysis") {
            normalizedRoute = "Email Analysis";
          } else if (normalizedRoute === "AlertDetail") {
            normalizedRoute = "Alerts Intelligence Hub";
          }

          if (setHeroContent) {
            setHeroContent(normalizedRoute);

            const needsUrlParams =
              normalizedRoute === "Email Analysis" ||
              normalizedRoute === "Scam Detection" ||
              (parsedAction.params &&
                Object.keys(parsedAction.params).some((key) =>
                  ["analysisKey", "autoSelect", "showDetails"].includes(key),
                ));

            if (
              needsUrlParams &&
              parsedAction.params &&
              Object.keys(parsedAction.params).length > 0
            ) {
              const searchParams = new URLSearchParams(parsedAction.params);
              window.history.pushState(
                {},
                "",
                `${window.location.pathname}?${searchParams.toString()}`,
              );
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent("notification-navigation", {
                    detail: { params: parsedAction.params },
                  }),
                );
                window.dispatchEvent(new CustomEvent("url-changed"));
              }, 100);
            }
          }
        } else if (parsedAction.type === "external_link" && parsedAction.url) {
          window.open(parsedAction.url, "_blank");
        }
      } catch (err) {
        console.error("Failed to handle notification action:", err);
        setVerificationFailedNotificationId(notificationId);
      }
    },
    [setHeroContent],
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: List View
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderListView = () => (
    <div className="easm-list-view">
      {/* Header */}
      <div className="easm-header">
        <div className="easm-header-top">
          <h1 className="easm-title">
            <FontAwesomeIcon icon={faBell} className="easm-title-icon" />
            {t("notification.title")}
          </h1>
        </div>
        <p className="easm-subtitle">{t("notification.subtitle")}</p>
      </div>

      {/* Mark All Read banner — shown only when there are unread */}
      {hasUnread && (
        <button
          className="easm-forward-banner nsm-mark-all-banner"
          onClick={handleMarkAllAsRead}
          type="button"
        >
          <div className="easm-forward-banner-content">
            <FontAwesomeIcon
              icon={faCheckDouble}
              className="easm-forward-icon"
            />
            <div className="easm-forward-text">
              <span className="easm-forward-label">
                {unreadCount === 1
                  ? t("notification.unreadCount.one")
                  : t("notification.unreadCount.many").replace(
                      "{count}",
                      unreadCount,
                    )}
              </span>
              <span className="easm-forward-email">
                {t("notification.tapToMarkAllRead")}
              </span>
            </div>
          </div>
          <div className="easm-forward-copy">
            <FontAwesomeIcon icon={faCheck} />
            <span>{t("notification.markAll")}</span>
          </div>
        </button>
      )}

      {/* Results header */}
      <div className="easm-results-header">
        <h2 className="easm-results-title">
          {t("notification.sections.all.title")}
          {safeNotifications.length > 0 && (
            <span className="easm-results-count">
              {safeNotifications.length}
            </span>
          )}
        </h2>
        <RefreshButton
          onClick={handleRefresh}
          loading={isRefreshing}
          size="sm"
        />
      </div>

      {/* Content */}
      {loading && safeNotifications.length === 0 ? (
        <div className="easm-empty-state">
          <p className="nsm-loading-text">{t("notification.loading")}</p>
        </div>
      ) : error ? (
        <div className="easm-empty-state">
          <p className="nsm-error-text">{error}</p>
          <button
            className="nsm-retry-btn"
            onClick={loadNotifications}
            type="button"
          >
            {t("common.retry")}
          </button>
        </div>
      ) : safeNotifications.length === 0 ? (
        <div className="easm-empty-state">
          <div className="nsm-empty-icon">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <p className="nsm-empty-title">{t("notification.allCaughtUp")}</p>
          <p className="nsm-empty-subtitle">
            {t("notification.noItemsToShow")}
          </p>
          <RefreshButton
            onClick={handleRefresh}
            label={t("notification.checkForUpdates")}
          />
        </div>
      ) : (
        <div className="notification-list nsm-notification-list">
          {safeNotifications.map((notification) => (
            <NotificationItem
              key={notification.notificationId}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDismiss={handleDismiss}
              onAction={handleNotificationAction}
              verificationFailed={
                notification.notificationId === verificationFailedNotificationId
              }
            />
          ))}
        </div>
      )}
    </div>
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MAIN RENDER
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  return (
    <div className="easm-container nsm-container">
      <div className="easm-view-slider">
        <div className="easm-slide easm-slide-list">{renderListView()}</div>
      </div>
    </div>
  );
}

export default NotificationSimpleMobile;
